import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Divider, TextField } from '@mui/material';
import { Button } from '@components/index';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { SearchIcon } from '@icons/index';
import type { WorkflowsFilter } from '@repositories/workflowRepository/workflow.types';
import { debouncedChangeFilter, useSearchParamsFilter } from '@customHooks/useSearchParamsFilter';
import { useGetWorkflows } from '@queryHooks/useWorkflow';
import { WorkflowListTable } from './WorkflowListTable';

export const WorkflowMgmtList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { authStore, workflowDetailStore } = useStore();
  const navigate = useNavigate();
  const [filter, setFilter] = useSearchParamsFilter<WorkflowsFilter>({
    defaultFilter: {
      name: '',
    },
    whiteList: ['name'],
  });
  const [searchName, setSearchName] = useState(filter.name);

  const { data: workflows, isLoading } = useGetWorkflows(filter);

  const workflowsList = workflows?.content || [];

  const handleClickWorkflowCreateBtn = () => {
    workflowDetailStore.setToolbarView('triggerSelect');
    navigate(`/manager/${authStore.curTenant.id}/workflows/management/create`);
  };

  const handleChangeFilter = (value: string) => {
    setFilter(prev => ({ ...prev, name: value }));
  };

  const handleSearchNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchName(event.target.value);
    debouncedChangeFilter(handleChangeFilter, event.target.value);
  };

  return (
    <Container>
      <Header>
        <strong>{t('Workflow.menu.list')}</strong>
      </Header>
      <Divider />
      <Body>
        <Toolbar>
          <strong className="toolbar-left">
            {t('Acc_Main_12')}
            <span className="count">{workflows?.totalElements}</span>
          </strong>
          <div className="toolbar-right">
            <StyledTextField
              fullWidth
              size="small"
              placeholder={t('Workflow_Main_02')}
              value={searchName}
              onChange={handleSearchNameChange}
              InputProps={{
                startAdornment: (
                  <IconWrapper>
                    <SearchIcon width={16} height={16} color={theme.colors['ic-gray-light']} />
                  </IconWrapper>
                ),
              }}
            />
            <Button size="small" variant="contain" paddingHorizontal={16} onClick={handleClickWorkflowCreateBtn}>
              {t('Workflow_Main_03')}
            </Button>
          </div>
        </Toolbar>
        <WorkflowListTable workflows={workflowsList} isLoading={isLoading} />
      </Body>
    </Container>
  );
};

const Container = styled('div')`
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
  padding: 0px 32px;
`;

const Header = styled('div')`
  margin: 28px 0px 14px 0px;
  strong {
    ${fonts.Headline5};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    margin-left: 8px;
  }
`;

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 32px 0px;
  gap: 12px;
`;

const Toolbar = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 32px;

  & .toolbar-left {
    ${fonts.Headline7};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    margin-top: 6px;
    margin-left: 8px;
    & .count {
      margin-left: 4px;
      color: ${({ theme: { colors } }) => colors['text-primary-light']};
    }
  }

  & .toolbar-right {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  & .MuiInputBase-root.MuiOutlinedInput-root {
    padding: 6px 8px;
  }
  & .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
`;

const IconWrapper = styled('div')`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
`;
